import { useState, useMemo, useCallback } from "react";

const useTasksAnalytics = (tasks = [], users = []) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Create a map of userId to user for quick lookup
  const userMap = useMemo(() => {
    const map = {};
    (users || []).forEach((user) => {
      map[user.userId] = user;
    });
    return map;
  }, [users]);

  // Get user name for a task
  const getUserName = useCallback(
    (userId) => {
      return userMap[userId]?.name || "Unknown User";
    },
    [userMap],
  );

  // Get user email for a task
  const getUserEmail = useCallback(
    (userId) => {
      return userMap[userId]?.email || "";
    },
    [userMap],
  );

  // Filtered and sorted tasks
  const processedTasks = useMemo(() => {
    let filtered = (tasks || []).filter((task) => {
      const searchLower = searchTerm.toLowerCase();
      const userName = getUserName(task.userId);
      const userEmail = getUserEmail(task.userId);
      return (
        task.taskId?.toLowerCase().includes(searchLower) ||
        task.name?.toLowerCase().includes(searchLower) ||
        task.userId?.toLowerCase().includes(searchLower) ||
        userName?.toLowerCase().includes(searchLower) ||
        userEmail?.toLowerCase().includes(searchLower) ||
        task.labels?.some((label) => label.toLowerCase().includes(searchLower))
      );
    });

    // Sort tasks
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle null/undefined values
      if (aValue == null) aValue = sortBy === "created" ? 0 : "";
      if (bValue == null) bValue = sortBy === "created" ? 0 : "";

      // Determine if numeric sort
      const isNumericSort = ["created", "deadline", "schedule"].includes(
        sortBy,
      );

      if (isNumericSort) {
        const numA = Number(aValue) || 0;
        const numB = Number(bValue) || 0;
        return sortOrder === "asc" ? numA - numB : numB - numA;
      } else if (sortBy === "completed") {
        // Boolean sort
        const boolA = aValue ? 1 : 0;
        const boolB = bValue ? 1 : 0;
        return sortOrder === "asc" ? boolA - boolB : boolB - boolA;
      } else {
        // String comparison
        const strA = String(aValue).toLowerCase();
        const strB = String(bValue).toLowerCase();
        return sortOrder === "asc"
          ? strA.localeCompare(strB)
          : strB.localeCompare(strA);
      }
    });

    return filtered;
  }, [tasks, searchTerm, sortBy, sortOrder, getUserName, getUserEmail]);

  // Paginated tasks
  const paginatedTasks = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return processedTasks.slice(startIndex, startIndex + rowsPerPage);
  }, [processedTasks, page, rowsPerPage]);

  // Handle sort
  const handleSort = (property) => {
    if (sortBy === property) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(property);
      setSortOrder("desc");
    }
    setPage(1);
  };

  return {
    // State
    searchTerm,
    sortBy,
    sortOrder,
    page,
    rowsPerPage,
    // Data
    processedTasks,
    paginatedTasks,
    userMap,
    // Handlers
    setSearchTerm: (value) => {
      setSearchTerm(value);
      setPage(1);
    },
    setRowsPerPage: (value) => {
      setRowsPerPage(value);
      setPage(1);
    },
    setPage,
    handleSort,
    // Helper functions
    getUserName,
    getUserEmail,
  };
};

export default useTasksAnalytics;
